import React, { useMemo } from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { TopSalesRepBiDto } from "../../../shared/dtos/bi/TopSalesRepBiDto";

interface TopSalesRepsWidgetProps {
  data?: TopSalesRepBiDto[];
}

const TopSalesRepsWidget: React.FC<TopSalesRepsWidgetProps> = ({
  data = [],
}) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      totalRevenue: Number(item.totalRevenue || 0),
      displayName: item.username || `Rep ${index + 1}`,
    }));
  }, [data]);

  const THEME_COLORS = [
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.primary.light,
  ];

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Top Sales Representatives
        </Typography>

        {chartData.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={theme.palette.neutral.dark}
                />
                <XAxis
                  dataKey="displayName"
                  tick={{ fontSize: 12, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(value)
                  }
                  tick={{ fontSize: 12, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 8,
                    border: `1px solid ${theme.palette.neutral.dark}`,
                  }}
                  itemStyle={{
                    color: theme.palette.neutral.light,
                    fontWeight: "bold",
                  }}
                  formatter={(value, _name, props) => [
                    formatCurrency(Number(value)),
                    `Revenue (${props.payload.transactionCount} txns)`,
                  ]}
                />
                <Bar
                  dataKey="totalRevenue"
                  fill={theme.palette.secondary.main}
                  barSize={40}
                >
                  {chartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={THEME_COLORS[index % THEME_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No sales representative data found.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TopSalesRepsWidget;
